import React, { useState } from 'react'

import { Button, Icon, Popover } from '@clientbase/clientbase-library'
import { Box, Stack, Typography } from '@mui/material'
import { Plan, useAuthContext } from 'context/AuthContext'

import { theme } from 'styles/theme'

type UpgradeProps = {
  plan?: Plan | undefined
  resourceName: string
  iconWrapperBackgroundColor?: string
  iconColor?: string
}

const planNameMapper = {
  basic: 'Start',
  pro: 'Grow',
  ultra: 'Scale',
  start: 'Start',
  grow: 'Grow',
  scale: 'Scale'
}

const Upgrade: React.FC<UpgradeProps> = ({
  plan,
  resourceName,
  iconWrapperBackgroundColor,
  iconColor
}) => {
  const { setModalPricing } = useAuthContext()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  let closeTimeout: NodeJS.Timeout

  // O claim `capabilities` saiu do JWT (SC-10878), então `capabilities` nasce
  // null e só é preenchido quando /v1/merchant_capabilities responde. Nessa
  // janela o chamador passa `plan` undefined, e o default 'basic' anunciava
  // "Plano Start" para um recurso de Grow. Sem plano conhecido não há o que
  // anunciar: o selo não aparece até o dado chegar.
  const planName = plan
    ? planNameMapper[plan as keyof typeof planNameMapper]
    : undefined
  // A partir daqui `plan` é sempre um Plan conhecido: planName só existe quando
  // ele veio e está no mapa.
  if (!plan || !planName) return null

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget)
    clearTimeout(closeTimeout)
  }

  const handleClose = () => {
    closeTimeout = setTimeout(() => {
      setAnchorEl(null)
    }, 200)
  }

  const open = Boolean(anchorEl)

  return (
    <>
      <Box
        onMouseEnter={handleOpen}
        onMouseLeave={handleClose}
        sx={{
          borderRadius: '999px',
          backgroundColor:
            iconWrapperBackgroundColor || theme.palette.info[200],
          width: '16px',
          height: '16px',
          minWidth: '16px',
          minHeight: '16px',
          display: 'grid',
          placeItems: 'center'
        }}
      >
        <Icon
          icon="arrowUpward"
          sx={{
            color: iconColor ? `${iconColor}!important` : '#fff!important',
            fontSize: '14px'
          }}
        />
      </Box>

      <Popover
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        sx={{ pointerEvents: 'none' }}
        PaperProps={{
          onMouseEnter: () => clearTimeout(closeTimeout),
          onMouseLeave: handleClose,
          sx: { pointerEvents: 'auto', ml: 1, mt: 1 }
        }}
      >
        <Stack p="10px" width="225px" gap="10px">
          <Typography variant="smBold" fontSize="18px">
            Plano {planName}
          </Typography>
          <Typography>
            Para usar esse recurso, você precisa aderir ao plano {planName},
            clique abaixo para mais informações
          </Typography>
          <Button
            startIcon="faCircleArrowUp"
            variantButton="pricing"
            onClick={(e) => {
              e.stopPropagation()
              setModalPricing({ isOpen: true, plan, resourceName })
            }}
          >
            Upgrade
          </Button>
        </Stack>
      </Popover>
    </>
  )
}

export default Upgrade
